import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BuildIcon from '@mui/icons-material/Build';
import ScienceIcon from '@mui/icons-material/Science';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InventoryIcon from '@mui/icons-material/Inventory';
import GroupIcon from '@mui/icons-material/Group';

// Admin Dashboard Home Component - Focus on Approvals
const AdminDashboardHome = () => {
  const [userName, setUserName] = useState('Admin');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserName(userDoc.data().nama || user.email || 'Admin');
          } else {
            setUserName(user.email || 'Admin');
          }
        } catch (error) {
          console.error("Error fetching user name:", error);
          setUserName(user.email || 'Admin');
        }
      } else {
        setUserName('Admin');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Define shortcut cards - Priority approvals first with highlighted colors
  const shortcutCards = [
    // High Priority - Approvals (with colored borders)
    {
      title: 'Persetujuan Alat',
      description: 'Tinjau dan setujui permintaan peminjaman alat',
      icon: <BuildIcon />,
      path: 'persetujuan-alat-admin',
      color: '#d32f2f', // Red - urgent
      priority: 'high',
      category: 'approval'
    },
    {
      title: 'Persetujuan Bahan',
      description: 'Tinjau dan setujui permintaan bahan',
      icon: <ScienceIcon />,
      path: 'persetujuan-bahan-admin',
      color: '#d32f2f', // Red - urgent
      priority: 'high',
      category: 'approval'
    },
    {
      title: 'Persetujuan Pengguna',
      description: 'Setujui pendaftaran pengguna baru',
      icon: <PersonAddIcon />,
      path: 'approve-users',
      color: '#d32f2f', // Red - urgent
      priority: 'high',
      category: 'approval'
    },
    // Medium Priority - Management
    {
      title: 'Manajemen Pengguna',
      description: 'Kelola data pengguna sistem',
      icon: <GroupIcon />,
      path: 'manajemen-pengguna',
      color: '#1976d2',
      priority: 'medium',
      category: 'management'
    },
    {
      title: 'Manajemen Instrumen',
      description: 'Kelola data instrumen laboratorium',
      icon: <InventoryIcon />,
      path: 'manajemen-instrumen',
      color: '#1976d2',
      priority: 'medium',
      category: 'management'
    },
    {
      title: 'Manajemen Alat Gelas',
      description: 'Kelola data alat gelas laboratorium',
      icon: <ScienceIcon />,
      path: 'manajemen-alat-gelas',
      color: '#1976d2',
      priority: 'medium',
      category: 'management'
    },
    {
      title: 'Manajemen Bahan',
      description: 'Kelola data bahan laboratorium',
      icon: <ScienceIcon />,
      path: 'manajemen-bahan',
      color: '#1976d2',
      priority: 'medium',
      category: 'management'
    },
    // Low Priority - Reports
    {
      title: 'Laporan Logbook',
      description: 'Lihat laporan logbook penggunaan alat',
      icon: <AssessmentIcon />,
      path: 'laporan-logbook',
      color: '#2e7d32',
      priority: 'low',
      category: 'report'
    },
    {
      title: 'Laporan Penggunaan Alat',
      description: 'Lihat laporan penggunaan alat',
      icon: <AssessmentIcon />,
      path: 'laporan-penggunaan-alat',
      color: '#2e7d32',
      priority: 'low',
      category: 'report'
    },
    {
      title: 'Laporan Penggunaan Bahan',
      description: 'Lihat laporan penggunaan bahan',
      icon: <AssessmentIcon />,
      path: 'laporan-penggunaan-bahan',
      color: '#2e7d32',
      priority: 'low',
      category: 'report'
    },
    {
      title: 'Laporan Kerusakan',
      description: 'Lihat laporan kerusakan alat',
      icon: <AssessmentIcon />,
      path: 'laporan-kerusakan',
      color: '#2e7d32',
      priority: 'low',
      category: 'report'
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  // Group cards by category for better organization
  const approvalCards = shortcutCards.filter(card => card.category === 'approval');
  const managementCards = shortcutCards.filter(card => card.category === 'management');
  const reportCards = shortcutCards.filter(card => card.category === 'report');

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 },
      maxWidth: '1400px',
      mx: 'auto',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Welcome Section with Logo */}
      <Box 
        sx={{ 
          mb: { xs: 1.5, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 1, sm: 2 }
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
              mb: { xs: 0.25, sm: 0.5 },
              lineHeight: 1.2
            }}
          >
            Selamat datang, {loading ? '...' : userName}!
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              fontSize: { xs: '0.8125rem', sm: '1rem' },
              lineHeight: 1.3
            }}
          >
            Dashboard Admin - Sistem Manajemen Laboratorium Rekayasa Kosmetik
          </Typography>
        </Box>
        
        {/* Logo - Top Right */}
        <Box 
          sx={{ 
            display: 'block',
            flexShrink: 0
          }}
        >
          <Box
            component="img"
            src="/images/LOGO PRODI REKOS.jpg"
            alt="Logo Prodi Rekayasa Kosmetik"
            sx={{
              maxWidth: { xs: '80px', sm: '120px' },
              height: 'auto',
              maxHeight: { xs: 50, sm: 80 },
              borderRadius: 1,
              display: 'block'
            }}
          />
        </Box>
      </Box>

      {/* Priority Approvals Section */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.25rem' },
            mb: { xs: 1.5, sm: 2.5 },
            color: 'error.main'
          }}
        >
          ⚠️ Persetujuan Prioritas
        </Typography>
        
        <Grid container spacing={{ xs: 1, sm: 2, md: 2.5 }}>
          {approvalCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
              <Card 
                elevation={0}
                sx={{ 
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: { xs: 1.5, sm: 2 },
                  border: `3px solid ${card.color}`,
                  backgroundColor: `${card.color}08`, // Light tint of the color
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: { xs: 'none', sm: 'translateY(-6px)' },
                    boxShadow: { xs: 2, sm: 8 },
                    borderColor: card.color,
                    backgroundColor: `${card.color}15`,
                    '& .card-icon': {
                      transform: { xs: 'none', sm: 'scale(1.15)' }
                    }
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(card.path)}
                  sx={{ 
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    p: { xs: 1, sm: 2 },
                    minHeight: { xs: 120, sm: 160, md: 170 },
                    width: '100%',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <Box 
                    className="card-icon"
                    sx={{ 
                      color: card.color,
                      mb: { xs: 0.75, sm: 1.5 },
                      transition: 'transform 0.3s ease',
                      fontSize: { xs: 32, sm: 48, md: 52 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {card.icon}
                  </Box>
                  <CardContent 
                    sx={{ 
                      textAlign: 'center', 
                      p: 0,
                      pt: 0,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: '100%',
                      minWidth: 0 // Prevent overflow
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      component="div" 
                      sx={{ 
                        fontWeight: 700,
                        fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
                        mb: { xs: 0.5, sm: 0.75 },
                        lineHeight: 1.3,
                        color: card.color,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        display: { xs: 'none', sm: 'block' },
                        lineHeight: 1.4,
                        px: 0.5,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Management Section */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.25rem' },
            mb: { xs: 1.5, sm: 2.5 },
            color: 'text.primary'
          }}
        >
          Manajemen
        </Typography>
        
        <Grid container spacing={{ xs: 1, sm: 2, md: 2.5 }}>
          {managementCards.map((card, index) => (
            <Grid item xs={6} sm={4} md={3} key={index} sx={{ display: 'flex' }}>
              <Card 
                elevation={0}
                sx={{ 
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: { xs: 1.5, sm: 2 },
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    transform: { xs: 'none', sm: 'translateY(-6px)' },
                    boxShadow: { xs: 2, sm: 6 },
                    borderColor: card.color,
                    '& .card-icon': {
                      transform: { xs: 'none', sm: 'scale(1.1)' }
                    }
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(card.path)}
                  sx={{ 
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    p: { xs: 1, sm: 2 },
                    minHeight: { xs: 120, sm: 150, md: 160 },
                    width: '100%',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <Box 
                    className="card-icon"
                    sx={{ 
                      color: card.color,
                      mb: { xs: 0.75, sm: 1.5 },
                      transition: 'transform 0.3s ease',
                      fontSize: { xs: 32, sm: 40, md: 44 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {card.icon}
                  </Box>
                  <CardContent 
                    sx={{ 
                      textAlign: 'center', 
                      p: 0,
                      pt: 0,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: '100%',
                      minWidth: 0 // Prevent overflow
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      component="div" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.8125rem', sm: '0.9375rem', md: '1rem' },
                        mb: { xs: 0, sm: 0.75 },
                        lineHeight: 1.3,
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        display: { xs: 'none', sm: 'block' },
                        lineHeight: 1.4,
                        px: 0.5,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Reports Section */}
      <Box>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.25rem' },
            mb: { xs: 1.5, sm: 2.5 },
            color: 'text.primary'
          }}
        >
          Laporan
        </Typography>
        
        <Grid container spacing={{ xs: 1, sm: 2, md: 2.5 }}>
          {reportCards.map((card, index) => (
            <Grid item xs={6} sm={4} md={3} key={index} sx={{ display: 'flex' }}>
              <Card 
                elevation={0}
                sx={{ 
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: { xs: 1.5, sm: 2 },
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    transform: { xs: 'none', sm: 'translateY(-6px)' },
                    boxShadow: { xs: 2, sm: 6 },
                    borderColor: card.color,
                    '& .card-icon': {
                      transform: { xs: 'none', sm: 'scale(1.1)' }
                    }
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(card.path)}
                  sx={{ 
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    p: { xs: 1, sm: 2 },
                    minHeight: { xs: 120, sm: 150, md: 160 },
                    width: '100%',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <Box 
                    className="card-icon"
                    sx={{ 
                      color: card.color,
                      mb: { xs: 0.75, sm: 1.5 },
                      transition: 'transform 0.3s ease',
                      fontSize: { xs: 32, sm: 40, md: 44 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {card.icon}
                  </Box>
                  <CardContent 
                    sx={{ 
                      textAlign: 'center', 
                      p: 0,
                      pt: 0,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: '100%',
                      minWidth: 0 // Prevent overflow
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      component="div" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.8125rem', sm: '0.9375rem', md: '1rem' },
                        mb: { xs: 0, sm: 0.75 },
                        lineHeight: 1.3,
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        display: { xs: 'none', sm: 'block' },
                        lineHeight: 1.4,
                        px: 0.5,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default AdminDashboardHome;

